import { useCallback, useEffect, useState } from "react";
import api, { formatPrice } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { BookOpen, Plus, Loader2 } from "lucide-react";

const fmtEur = (v) => formatPrice(v);

const fmtDate = (d) => (d ? `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}` : "—");
const SOURCE_LABEL = { rechnung: "Rechnung (automatisch)", manuell: "manuell" };

export default function CashBookSection() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ type: "einnahme", amount_gross: "", description: "", reference: "", entry_date: "" });
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/cash-book");
            setData(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kassenbuch konnte nicht geladen werden");
        }
        setLoading(false);
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        const amt = parseFloat(String(form.amount_gross).replace(",", "."));
        if (!amt || amt <= 0 || !form.description.trim()) {
            toast.error("Bitte Betrag und Beschreibung angeben"); return;
        }
        setSaving(true);
        try {
            await api.post("/cash-book", {
                type: form.type, amount_gross: amt,
                description: form.description.trim(),
                reference: form.reference || null,
                entry_date: form.entry_date || null,
            });
            toast.success("Eintrag gebucht");
            setForm({ type: "einnahme", amount_gross: "", description: "", reference: "", entry_date: "" });
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Buchung fehlgeschlagen"); }
        setSaving(false);
    };

    return (
        <div className="space-y-4" data-testid="cash-book-section">
            <div className="flex flex-wrap items-center gap-3">
                <h3 className="font-black text-[#111827] inline-flex items-center gap-2">
                    <BookOpen className="w-4 h-4" /> Kassenbuch
                </h3>
                <div className="ml-auto bg-[#111827] text-[#FFCC00] font-black rounded-sm px-3 py-1.5 text-sm" data-testid="cash-book-balance">
                    Kassenbestand: {fmtEur(data?.balance || 0)}
                </div>
            </div>
            <div className="text-xs text-gray-500">
                Barzahlungen aus Rechnungen (Zahlart „Barzahlung") werden automatisch als Einnahme gebucht.
                Einträge werden nicht gelöscht — Korrekturen per Gegenbuchung (Ausgabe/Einnahme).
            </div>

            {/* Manuelle Buchung */}
            <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 flex flex-wrap items-end gap-2" data-testid="cash-book-form">
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Typ</div>
                    <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                        className="h-9 border border-gray-300 rounded-sm px-2 text-sm bg-white" data-testid="cash-book-type">
                        <option value="einnahme">Einnahme</option>
                        <option value="ausgabe">Ausgabe</option>
                    </select>
                </div>
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Betrag (brutto)</div>
                    <Input value={form.amount_gross} onChange={(e) => setForm({ ...form, amount_gross: e.target.value })}
                        placeholder="0,00" className="h-9 w-28 rounded-sm" data-testid="cash-book-amount" />
                </div>
                <div className="flex-1 min-w-[180px]">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Beschreibung</div>
                    <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                        placeholder="z. B. Porto, Wechselgeld-Einlage…" className="h-9 rounded-sm" data-testid="cash-book-description" />
                </div>
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Beleg/Referenz</div>
                    <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}
                        placeholder="optional" className="h-9 w-32 rounded-sm" data-testid="cash-book-reference" />
                </div>
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Datum</div>
                    <Input type="date" value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })}
                        className="h-9 w-36 rounded-sm" data-testid="cash-book-date" />
                </div>
                <Button onClick={save} disabled={saving} className="h-9 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black font-bold" data-testid="cash-book-save">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Buchen
                </Button>
            </div>

            {/* Einträge */}
            {loading ? (
                <div className="text-sm text-gray-500 py-6 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Lädt…</div>
            ) : !(data?.entries || []).length ? (
                <div className="text-sm text-gray-500 py-6" data-testid="cash-book-empty">Noch keine Kassenbuch-Einträge.</div>
            ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-sm">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500">
                                <th className="px-3 py-2">Datum</th>
                                <th className="px-3 py-2">Beschreibung</th>
                                <th className="px-3 py-2">Referenz</th>
                                <th className="px-3 py-2">Quelle</th>
                                <th className="px-3 py-2 text-right">Einnahme</th>
                                <th className="px-3 py-2 text-right">Ausgabe</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.entries.map((e) => (
                                <tr key={e.id} className="border-t border-gray-100" data-testid={`cash-book-row-${e.id}`}>
                                    <td className="px-3 py-2 whitespace-nowrap">{fmtDate(e.entry_date)}</td>
                                    <td className="px-3 py-2">{e.description}</td>
                                    <td className="px-3 py-2 text-gray-500">{e.reference || "—"}</td>
                                    <td className="px-3 py-2 text-gray-500 text-xs">{SOURCE_LABEL[e.source] || e.source}</td>
                                    <td className="px-3 py-2 text-right font-bold text-emerald-700">
                                        {e.type === "einnahme" ? fmtEur(e.amount_gross) : ""}
                                    </td>
                                    <td className="px-3 py-2 text-right font-bold text-red-700">
                                        {e.type === "ausgabe" ? fmtEur(e.amount_gross) : ""}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="border-t-2 border-[#111827] bg-gray-50 font-black">
                                <td className="px-3 py-2" colSpan={4}>Summe (Zeitraum)</td>
                                <td className="px-3 py-2 text-right text-emerald-700">{fmtEur(data.period_in || 0)}</td>
                                <td className="px-3 py-2 text-right text-red-700">{fmtEur(data.period_out || 0)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    );
}
